import React from 'react';
import moment from 'moment';
import { getLocalTimezoneLabel } from '../../../../utils/datetime';
import { Link, useSearchParams } from 'react-router-dom';
import {
  BellIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { selectBlurSensitive } from '../../../../slices/preferencesSlice';

const activityIcons = {
  login: ArrowRightOnRectangleIcon,
  notification_update: BellIcon,
  forecast_submission: ChartBarIcon,
  forecast_continuous: ChartBarIcon,
  file_export: DocumentArrowDownIcon,
  profile_update: UserCircleIcon,
};

const activityColors = {
  login: 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400',
  notification_update: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
  forecast_submission: 'bg-brand-50 text-brand-600 dark:bg-brand-900/20 dark:text-brand-400',
  forecast_continuous: 'bg-brand-50 text-brand-600 dark:bg-brand-900/20 dark:text-brand-400',
  file_export: 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400',
  profile_update: 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400',
};

// Where a login came from (login.py metadata.source). Rows created before the
// source field existed have no value and are shown as "Unknown".
const loginSourceLabels = { web: 'Web', api: 'API' };
const loginSourceColors = {
  web: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  api: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};
const UNKNOWN_SOURCE_COLOR = 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';

export default function ActivityCard({ activity }) {
  const blurSensitive = useSelector(selectBlurSensitive);
  // When "Hide sensitive information" is on, swap the displayed identity to the
  // user's docker-style username alias (looked up from the already-loaded
  // usersList on the parent page). The serializer doesn't expose `user_username`
  // here, so we resolve it client-side — falls back to `user_name` if the user
  // isn't in the list yet, which self-heals once usersList loads.
  const usersList = useSelector((state) => state.user?.usersList || []);
  const [searchParams] = useSearchParams();
  const Icon = activityIcons[activity.activity_type] || UserCircleIcon;
  const colorClass = activityColors[activity.activity_type] || 'bg-gray-50 text-gray-600';

  const displayName = blurSensitive
    ? (usersList.find((u) => u.id === activity.user)?.username || activity.user_name)
    : activity.user_name;

  // Build URL with current filters to preserve state when navigating back
  const buildUserProfileUrl = () => {
    const params = new URLSearchParams();

    // Add source indicator
    params.set('source', 'activity');

    // Preserve current UserActivity filters
    if (searchParams.get('user')) params.set('activityUser', searchParams.get('user'));
    if (searchParams.get('type')) params.set('activityType', searchParams.get('type'));
    if (searchParams.get('start')) params.set('activityStart', searchParams.get('start'));
    if (searchParams.get('end')) params.set('activityEnd', searchParams.get('end'));
    if (searchParams.get('sort')) params.set('activitySort', searchParams.get('sort'));
    if (searchParams.get('page')) params.set('activityPage', searchParams.get('page'));
    if (searchParams.get('pageSize')) params.set('activityPageSize', searchParams.get('pageSize'));

    return `/users/${activity.user}?${params.toString()}`;
  };

  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-0 py-3">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${colorClass}`}>
          <Icon className="w-4 h-4" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* User info and time in one line */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Link
                to={buildUserProfileUrl()}
                className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline decoration-blue-600/30 dark:decoration-blue-400/30 hover:decoration-blue-800 dark:hover:decoration-blue-300 underline-offset-2 truncate transition-all"
              >
                {displayName}
              </Link>
              <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                {moment(activity.created_at).fromNow()}
              </span>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {activity.description}
          </p>

          {/* Metadata badges and timestamp */}
          <div className="flex items-center gap-2 flex-wrap">
            {activity.activity_type === 'login' && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${loginSourceColors[activity.metadata?.source] || UNKNOWN_SOURCE_COLOR}`}>
                {loginSourceLabels[activity.metadata?.source] || 'Unknown'}
              </span>
            )}
            {activity.metadata && Object.keys(activity.metadata).length > 0 && (
              <>
                {activity.activity_type === 'file_export' && activity.metadata.export_type && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {activity.metadata.export_type}
                  </span>
                )}
                {activity.activity_type.includes('forecast') && activity.metadata.variable && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {activity.metadata.variable.toUpperCase()}
                  </span>
                )}
              </>
            )}
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {moment(activity.created_at).format('MMM D, YYYY [at] h:mm A')}{' '}
              {getLocalTimezoneLabel(activity.created_at)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
